import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type ClientOption = {
  readonly id: number
  readonly slug: string
  readonly display_name: string
  readonly color_hex: string
}

type ContactSummary = {
  readonly id: number
  readonly email: string
  readonly first_name: string | null
  readonly last_name: string | null
  readonly title: string | null
  readonly phone: string | null
  readonly linkedin_url: string | null
}

type OpportunityCard = {
  readonly id: number
  readonly name: string
  readonly coarse_stage: string
  readonly lifecycle_stage_id: string
  readonly status: string
  readonly service_line: string
  readonly value_cents: number
  readonly currency: string
  readonly primary_contact: { readonly id: number; readonly name: string; readonly email: string } | null
}

type ReplySummary = {
  readonly id: number
  readonly lead_name: string
  readonly reply_subject: string
  readonly received_at: string | null
  readonly status: string
  readonly client: ClientOption | null
}

type AccountDetail = {
  readonly id: number
  readonly name: string
  readonly domain: string
  readonly website: string
  readonly linkedin_url: string
  readonly industry: string
  readonly employee_range: string
  readonly icp_fit: string
  readonly notes: string
  readonly summary: string
  readonly sample_list_url: string
  readonly client: ClientOption
}

type AccountShowProps = {
  readonly account: AccountDetail
  readonly contacts: readonly ContactSummary[]
  readonly opportunities: readonly OpportunityCard[]
  readonly recent_replies: readonly ReplySummary[]
  readonly clients: readonly ClientOption[]
}

const EMPLOYEE_RANGES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"]
const ICP_FIT_OPTIONS = ["unknown", "perfect", "good", "potential", "low"]

export default function AccountShow({ account, contacts, opportunities, recent_replies }: AccountShowProps) {
  const { props } = usePage<PageProps<AccountShowProps>>()
  const name = account.name || account.domain
  const description = `CRM account ${name} — contacts, opportunities, and recent replies.`

  const [editing, setEditing] = React.useState(false)
  const [form, setForm] = React.useState({
    name: account.name,
    website: account.website,
    linkedin_url: account.linkedin_url,
    industry: account.industry,
    employee_range: account.employee_range,
    icp_fit: account.icp_fit,
    notes: account.notes,
    summary: account.summary,
    sample_list_url: account.sample_list_url,
  })

  const save = () => {
    router.patch(`/crm/accounts/${account.id}`, { account: form }, { preserveScroll: true })
    setEditing(false)
  }

  const cancel = () => {
    setForm({
      name: account.name,
      website: account.website,
      linkedin_url: account.linkedin_url,
      industry: account.industry,
      employee_range: account.employee_range,
      icp_fit: account.icp_fit,
      notes: account.notes,
      summary: account.summary,
      sample_list_url: account.sample_list_url,
    })
    setEditing(false)
  }

  return (
    <>
      <Head title={name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={name} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1>{name}</h1>
              <div className="mt-2 flex items-center gap-2 text-sm text-ink-muted">
                <span
                  className="h-2.5 w-2.5 rounded-full border border-hairline"
                  style={{ backgroundColor: account.client.color_hex }}
                />
                {account.client.display_name}
                {account.domain && <span>· {account.domain}</span>}
              </div>
            </div>
            {!editing ? (
              <Button asChild variant="secondary">
                <button type="button" onClick={() => setEditing(true)}>Edit</button>
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button type="button" onClick={save}>Save</Button>
                <Button type="button" variant="secondary" onClick={cancel}>Cancel</Button>
              </div>
            )}
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <section className="rounded-lg border border-hairline bg-page px-6">
            <DataTable>
              {editing ? (
                <>
                  <DataRow title="Name">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </DataRow>
                  <DataRow title="Website">
                    <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
                  </DataRow>
                  <DataRow title="LinkedIn URL">
                    <Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/company/" />
                  </DataRow>
                  <DataRow title="Industry">
                    <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                  </DataRow>
                  <DataRow title="Employee range">
                    <Select
                      value={form.employee_range}
                      onChange={(e) => setForm({ ...form, employee_range: e.target.value })}
                    >
                      <option value="">Unknown</option>
                      {EMPLOYEE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </Select>
                  </DataRow>
                  <DataRow title="ICP fit">
                    <Select
                      value={form.icp_fit}
                      onChange={(e) => setForm({ ...form, icp_fit: e.target.value })}
                    >
                      {ICP_FIT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </Select>
                  </DataRow>
                  <DataRow title="Notes">
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </DataRow>
                  <DataRow title="Summary">
                    <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
                  </DataRow>
                  <DataRow title="Sample list URL">
                    <Input value={form.sample_list_url} onChange={(e) => setForm({ ...form, sample_list_url: e.target.value })} placeholder="https://" />
                  </DataRow>
                </>
              ) : (
                <>
                  <DataRow title="Domain">{account.domain}</DataRow>
                  <DataRow title="Website">
                    {account.website ? (
                      <a href={account.website} target="_blank" rel="noopener noreferrer" className="no-underline hover:underline">
                        {account.website}
                      </a>
                    ) : "—"}
                  </DataRow>
                  <DataRow title="LinkedIn">
                    {account.linkedin_url ? (
                      <a href={account.linkedin_url} target="_blank" rel="noopener noreferrer" className="no-underline hover:underline">
                        View
                      </a>
                    ) : "—"}
                  </DataRow>
                  <DataRow title="Industry">{account.industry || "—"}</DataRow>
                  <DataRow title="Employees">{account.employee_range || "—"}</DataRow>
                  <DataRow title="ICP fit">{account.icp_fit}</DataRow>
                  {account.notes && <DataRow title="Notes">{account.notes}</DataRow>}
                  {account.summary && <DataRow title="Summary">{account.summary}</DataRow>}
                  <DataRow title="Sample list">
                    {account.sample_list_url ? (
                      <a href={account.sample_list_url} target="_blank" rel="noopener noreferrer" className="no-underline hover:underline">
                        View
                      </a>
                    ) : "—"}
                  </DataRow>
                </>
              )}
            </DataTable>
          </section>

          <section className="flex flex-col gap-4">
            <h2>Contacts</h2>
            {contacts.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {contacts.map((contact) => (
                    <Link
                      key={contact.id}
                      href={`/contacts/${contact.id}`}
                      className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-4"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-ink-display">
                          {contactName(contact)}
                        </div>
                        {contact.title && (
                          <div className="mt-0.5 text-sm text-ink-muted">{contact.title}</div>
                        )}
                      </div>
                      <div className="truncate text-sm text-ink-muted">
                        {contact.email}
                      </div>
                      <div className="flex gap-2 sm:justify-end">
                        {contact.phone && <Badge tone="muted">{contact.phone}</Badge>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No contacts linked to this account.</p>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <h2>Opportunities</h2>
            {opportunities.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {opportunities.map((opp) => (
                    <Link
                      key={opp.id}
                      href={`/crm/opportunities/${opp.id}`}
                      className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-ink-display">{opp.name}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                          {opp.primary_contact && <span>{opp.primary_contact.name}</span>}
                          {opp.value_cents > 0 && (
                            <span>{new Intl.NumberFormat("en-US", { style: "currency", currency: opp.currency.toUpperCase() }).format(opp.value_cents / 100)}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 sm:justify-end">
                        <Badge tone="muted">{opp.coarse_stage}</Badge>
                        <Badge tone="muted">{opp.status}</Badge>
                        <Badge
                          tone={opp.service_line === "leadgen" ? "signal" : "accent"}
                        >
                          {opp.service_line}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No opportunities for this account.</p>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <h2>Recent replies</h2>
            {recent_replies.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {recent_replies.map((reply) => (
                    <Link
                      key={reply.id}
                      href={`/inbox?selected=${reply.id}`}
                      className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-ink-display">
                          {reply.reply_subject || reply.lead_name}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                          {reply.client && (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full border border-hairline"
                                style={{ backgroundColor: reply.client.color_hex }}
                              />
                              {reply.client.display_name}
                            </span>
                          )}
                          <span>{formatDateTime(reply.received_at)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 sm:justify-end">
                        <Badge tone="muted">{reply.status}</Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No recent replies from this account.</p>
            )}
          </section>
        </div>
      </AppShell>
    </>
  )
}

function contactName(contact: ContactSummary) {
  const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
  return name || contact.email
}

function formatDateTime(value: string | null) {
  if (!value) return "Unknown"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value))
}
